import { PLATFORM_GLOBAL_ROLE_NAMES } from './protected-global-role.js';
/**
 * Case-normalized so a tenant claim cannot smuggle a platform-global role past
 * this filter by varying case. Every sibling predicate in protected-global-role
 * already lowercases (isProtectedGlobalRoleName, isScopeBypassRoleName); an
 * exact-case set here was the odd one out, and isScopeBypassRoleName is
 * case-insensitive and consumed by require-scope.guard, so one refactor feeding
 * it resolved roles would have turned this into a live bypass.
 */
const PLATFORM_GLOBAL_ROLE_NAME_SET = new Set(PLATFORM_GLOBAL_ROLE_NAMES.map((roleName) => roleName.toLowerCase()));
function normalizeRoleName(value) {
    const roleName = typeof value === 'string'
        ? value
        : value && typeof value === 'object' && !Array.isArray(value)
            ? typeof value.name === 'string'
                ? value.name
                : ''
            : '';
    return roleName && roleName === roleName.trim() ? roleName : undefined;
}
function normalizeRoleList(value, acceptSingle = false) {
    if (acceptSingle && typeof value === 'string') {
        const roleName = normalizeRoleName(value);
        return roleName ? [roleName] : [];
    }
    if (!Array.isArray(value)) {
        return value === undefined ? [] : undefined;
    }
    const roleNames = value.map(normalizeRoleName);
    if (roleNames.some((roleName) => roleName === undefined)) {
        return undefined;
    }
    return roleNames;
}
/**
 * Normalizes a global role claim, discarding only the unreadable entries.
 *
 * Global roles degrade entry by entry rather than as a list: a single
 * unreadable entry must not revoke the remaining signed authority, which would
 * lock a valid operator out of every surface at once. Discarding can only
 * remove a role, never introduce one, so this cannot widen authority. Tenant
 * authority keeps the stricter whole-list rejection in normalizeTenantRoleList.
 */
function normalizeGlobalRoleList(value) {
    if (typeof value === 'string') {
        const roleName = normalizeRoleName(value);
        return roleName ? [roleName] : [];
    }
    if (!Array.isArray(value)) {
        return [];
    }
    return value
        .map(normalizeRoleName)
        .filter((roleName) => roleName !== undefined);
}
function normalizeTenantRoleList(value) {
    const roleNames = normalizeRoleList(value);
    return roleNames?.some((roleName) => PLATFORM_GLOBAL_ROLE_NAME_SET.has(roleName.toLowerCase()))
        ? undefined
        : roleNames;
}
function resolveSoleSignedTenantId(value) {
    const tenantIds = normalizeSignedTenantIds(value);
    return tenantIds?.length === 1 ? tenantIds[0] : undefined;
}
function normalizeSignedTenantIds(value) {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const tenantIds = value.map((tenantId) => typeof tenantId === 'string' && tenantId === tenantId.trim() ? tenantId : undefined);
    if (tenantIds.some((tenantId) => !tenantId)) {
        return undefined;
    }
    return Array.from(new Set(tenantIds));
}
/**
 * Retains only valid tenant-role entries covered by the signed tenant claim.
 *
 * This is intended for profile projection. It does not make the returned roles
 * globally effective; authorization must still use resolveAuthenticatedRoleNames.
 */
export function selectSignedTenantRoleNames(tenantIds, tenantRoles) {
    if (!tenantRoles || typeof tenantRoles !== 'object' || Array.isArray(tenantRoles)) {
        return {};
    }
    const normalizedTenantIds = normalizeSignedTenantIds(tenantIds);
    if (!normalizedTenantIds) {
        return {};
    }
    const signedTenantIds = new Set(normalizedTenantIds);
    return Object.fromEntries(Object.entries(tenantRoles)
        .filter(([tenantId]) => signedTenantIds.has(tenantId))
        .flatMap(([tenantId, roles]) => {
        const roleNames = normalizeTenantRoleList(roles);
        return roleNames ? [[tenantId, Array.from(new Set(roleNames))]] : [];
    }));
}
function resolveBoundTenantRoles(tenantId, value) {
    if (!tenantId || !value || typeof value !== 'object' || Array.isArray(value)) {
        return [];
    }
    const tenantRoles = value;
    const tenantKeys = Object.keys(tenantRoles);
    if (tenantKeys.length !== 1 || tenantKeys[0] !== tenantId) {
        return [];
    }
    return normalizeTenantRoleList(tenantRoles[tenantId]);
}
/**
 * Resolves authorization roles without flattening tenant authority globally.
 *
 * Tenant roles contribute only when the signed claim is bound to one tenant
 * and the role map contains exactly that tenant. Ambiguous, unsigned, or
 * malformed tenant authority fails closed while valid global roles remain.
 */
export function resolveAuthenticatedRoleNames(principal) {
    const globalRoles = resolveGlobalRoleNames(principal);
    const tenantId = resolveSoleSignedTenantId(principal?.tenantIds);
    const tenantRoles = resolveBoundTenantRoles(tenantId, principal?.tenantRoles) ?? [];
    return Array.from(new Set([...globalRoles, ...tenantRoles]));
}
/** Resolves only global roles; tenant-scoped authority never contributes. */
export function resolveGlobalRoleNames(principal) {
    return normalizeGlobalRoleList(principal?.roles);
}
/**
 * Reports whether the principal presents any tenant-role claim.
 *
 * This is deliberately independent of whether those claims were honored by
 * resolveAuthenticatedRoleNames. A caller that opens a fallback when no role
 * is present must consult this rather than the resolved role list, otherwise
 * an ambiguous claim that correctly fails closed would empty the role list and
 * reopen that fallback, letting a second tenant grant more authority than one.
 *
 * Absent means exactly: undefined, null, an empty array, or an empty plain
 * object. Everything else reports as presented, including shapes whose entries
 * Object.keys cannot enumerate such as a Map or a class instance. The fail
 * direction is deliberate — an unreadable claim must not read as no claim.
 */
export function presentsTenantRoleClaims(principal) {
    const tenantRoles = principal?.tenantRoles;
    if (tenantRoles === undefined || tenantRoles === null) {
        return false;
    }
    if (Array.isArray(tenantRoles)) {
        return tenantRoles.length > 0;
    }
    if (typeof tenantRoles === 'object') {
        const prototype = Object.getPrototypeOf(tenantRoles);
        if (prototype === Object.prototype || prototype === null) {
            // A plain object is the shape every minting path produces, so an empty
            // one genuinely means "no tenant claim". Symbols are counted too: they
            // are own properties Object.keys does not report, and a claim carrying
            // only symbol keys is still a claim.
            return (Object.keys(tenantRoles).length > 0 ||
                Object.getOwnPropertySymbols(tenantRoles).length > 0);
        }
        // Map, Set, or a class instance carries entries Object.keys cannot see.
        // Reporting those as absent would understate a presented claim, so treat
        // any non-plain object as presented.
        return true;
    }
    // Any other shape is malformed rather than absent. Report it as presented so
    // an unreadable claim fails closed instead of reopening a fallback that
    // exists only for principals carrying no tenant claim at all.
    return true;
}
//# sourceMappingURL=authenticated-user-roles.js.map